import PropTypes from 'prop-types';
import { Grid } from '@ui-propulsion/react-components';
import { useState, useEffect } from 'react';
import Head from 'next/head';
import Breadcumbs from '../breadcrumbs/breadcrumbs';
import styles from './blogProduct.module.scss';
import BlogPostList from '../blogPostList/blogPostList';
import LcResourcesArticleItem from '../lcResourcesArticleItem/lcResourcesArticleItem';
import Pagination from '../pagination/pagination';
import GlossaryHubPrequal from '../glossaryHubPrequal/glossaryHubPrequal';
import HelpCenterSearch from '../helpCenterSearch/helpCenterSearch';

function BlogProduct({ pageTitle, breadcumbs, posts, prequal, canonicalUrl }) {
  const [latestPosts, setLatestPosts] = useState([]);
  const [visiblePosts, setVisiblePosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const highlitedPosts = 4;
  const postsPerPage = 9;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const pageNo = searchParams.get('page') ?? 1;
    const startIndex = (pageNo - 1) * postsPerPage + highlitedPosts;
    const endIndex = startIndex + postsPerPage;

    setCurrentPage(pageNo);
    setLatestPosts(posts.slice(0, highlitedPosts));
    setVisiblePosts(posts.slice(startIndex, endIndex));
    setTotalPages(Math.ceil((posts.length - highlitedPosts) / postsPerPage));
  }, [posts]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const startIndex = (pageNumber - 1) * postsPerPage + highlitedPosts;
    const endIndex = startIndex + postsPerPage;
    setVisiblePosts(posts.slice(startIndex, endIndex));
    const params = new URLSearchParams(window.location.search);
    params.set('page', pageNumber);
    window.history.pushState(null, '', `?${params.toString()}`);
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  };

  const searchBox = {
    placeholder: 'Search',
    onEnter: (query) => {
      window.location.assign(`/resource-center/search?query=${query}`);
    },
  };

  const notFoundText = `We're sorry. We couldn't find anything using your search term.`;

  return (
    <div className={styles.blogProduct}>
      {canonicalUrl && (
        <Head>
          {currentPage > 1 && (
            <link rel="prev" href={`${canonicalUrl}?page=${currentPage - 1}`} />
          )}
          {currentPage < totalPages && (
            <link rel="next" href={`${canonicalUrl}?page=${currentPage + 1}`} />
          )}
        </Head>
      )}
      <div className={styles.bgGrey}>
        <div className={styles.container}>
          <div className={styles.section}>
            <div className={styles.breadcrumbs}>
              <Breadcumbs links={breadcumbs} />
            </div>
            <Grid container spacing={2} className={styles.search}>
              <Grid item xs={12} sm={8} className={styles.titleContainer}>
                <h1 className={styles.title}>{`${pageTitle}${
                  currentPage !== 1 ? ` Page ${currentPage}` : ''
                }`}</h1>
              </Grid>
              <Grid item xs={12} sm={4}>
                <HelpCenterSearch {...searchBox} />
              </Grid>
            </Grid>

            {posts?.length === 0 ? (
              <p className={styles.subTitle}>{notFoundText}</p>
            ) : (
              currentPage === 1 && (
                <BlogPostList items={latestPosts} showListThumbnail={false} />
              )
            )}
          </div>
        </div>
      </div>
      {visiblePosts.length > 0 && (
        <div className={styles.container}>
          <div className={styles.section}>
            <Grid container>
              <Grid item xs={12} md={8} className={styles.itemsContainer}>
                {visiblePosts?.map((item, index) => {
                  const key = `item-${index}`;
                  return (
                    <div className={styles.item} key={key}>
                      <LcResourcesArticleItem {...item} />
                    </div>
                  );
                })}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                {prequal && (
                  <div className={styles.prequal}>
                    <GlossaryHubPrequal {...prequal} />
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        </div>
      )}
    </div>
  );
}

BlogProduct.defaultProps = {
  product: null,
  posts: [],
  prequal: {},
  pageTitle: '',
  breadcumbs: [],
  canonicalUrl: null,
};

BlogProduct.propTypes = {
  product: PropTypes.string,
  posts: PropTypes.array,
  prequal: PropTypes.object,
  pageTitle: PropTypes.string,
  breadcumbs: PropTypes.array,
  canonicalUrl: PropTypes.string,
};

export default BlogProduct;
